import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import * as Icons from 'lucide-react';
import ServiceCard from '../components/ServiceCard';
import { services, whyChooseUs } from '../utils/mock';

const Home = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="space-y-8 animate-fade-in">
            <div className="inline-block px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-medium mb-6">
              Empowering the Next Generation of Innovators
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
              Building the Future with
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600">
                AI, Robotics & Innovation
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto">
              Empowering Students with Real-World Technology Skills Through Hands-On Learning and Expert Guidance
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8">
              <Link
                to="/contact"
                className="px-8 py-4 bg-cyan-500 text-white rounded-lg font-semibold hover:bg-cyan-600 transform hover:scale-105 transition-all duration-300 shadow-lg shadow-cyan-500/30 flex items-center space-x-2 group"
              >
                <span>Book a Workshop</span>
                <ArrowRight className="group-hover:translate-x-1 transition-transform duration-300" size={20} />
              </Link>
              <Link
                to="/services"
                className="px-8 py-4 bg-white/5 backdrop-blur-sm text-white border border-cyan-500/30 rounded-lg font-semibold hover:bg-white/10 transform hover:scale-105 transition-all duration-300"
              >
                Explore Services
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-cyan-400/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto space-y-6">
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              About <span className="text-cyan-400">FutureBuildX</span>
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              FutureBuildX is a pioneering AI, Robotics, and IoT training company dedicated to transforming education
              through practical technology skills. Operating at the state level, we empower school and college students
              with hands-on experience in cutting-edge technologies, preparing them to become the innovators and leaders
              of tomorrow.
            </p>
            <p className="text-lg text-gray-400 leading-relaxed">
              Our mission is to bridge the gap between theoretical knowledge and practical application, creating an
              innovation ecosystem where students can explore, experiment, and excel in emerging technologies.
            </p>
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our <span className="text-cyan-400">Services</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Comprehensive training and services designed to prepare students for the future of technology
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 font-semibold transition-colors duration-300 group"
            >
              <span>View All Services</span>
              <ArrowRight className="group-hover:translate-x-1 transition-transform duration-300" size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Why Choose <span className="text-cyan-400">Us</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              What makes FutureBuildX the preferred choice for technology education
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item) => {
              const IconComponent = Icons[item.icon.charAt(0).toUpperCase() + item.icon.slice(1)] || Icons.Star;
              return (
                <div
                  key={item.id}
                  className="bg-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-xl p-6 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20 group"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-400/20 to-blue-600/20 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="text-cyan-400" size={24} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-blue-500/10"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform Education?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Partner with us to bring cutting-edge technology education to your institution
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center space-x-2 px-8 py-4 bg-cyan-500 text-white rounded-lg font-semibold hover:bg-cyan-600 transform hover:scale-105 transition-all duration-300 shadow-lg shadow-cyan-500/30 group"
          >
            <span>Get in Touch</span>
            <ArrowRight className="group-hover:translate-x-1 transition-transform duration-300" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
